import * as React from 'react';
import { SearchIcon } from 'lucide-react';

import { cn } from '@components/utils/classname';

import { SelectSearchContext } from './context';

/**
 * SelectSearch - Search input for Select component
 * Place this as the first child of SelectContent to enable search functionality
 */
function SelectSearch({
  className,
  placeholder = 'Search...',
  onSearch,
  ...props
}: Omit<React.ComponentProps<'input'>, 'type' | 'value' | 'onChange'> & {
  placeholder?: string;
  onSearch?: (value: string) => void;
}) {
  const context = React.useContext(SelectSearchContext);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Auto-focus input when mounted
  React.useEffect(() => {
    if (!context) return;
    // Use a small delay to ensure the popover is fully rendered
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [context]);

  if (!context) {
    console.warn(
      'SelectSearch must be used within a Select component with search enabled',
    );
    return null;
  }

  const { searchValue, setSearchValue } = context;

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    onSearch?.(e.target.value);
  };

  return (
    <div
      data-slot="select-search-wrapper"
      className="border-border m-2 mb-0 flex h-8 items-center gap-2 rounded-md border px-3"
    >
      <SearchIcon className="size-4 shrink-0 opacity-50" />
      <input
        ref={inputRef}
        type="text"
        data-slot="select-search"
        className={cn(
          'placeholder:text-muted-foreground text-body2 flex w-full bg-transparent outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        placeholder={placeholder}
        value={searchValue}
        onChange={handleSearch}
        onKeyDown={(e) => {
          // Prevent Select from closing when typing
          e.stopPropagation();
        }}
        {...props}
      />
    </div>
  );
}

export { SelectSearch };
